import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import type { ProfessionalClientListResponse, ProfessionalInviteResponse } from '@/lib/types';

function coparentDisplayName(inv: ProfessionalInviteResponse): string {
  if (inv.coparent_name?.trim()) return inv.coparent_name.trim();
  return 'Co-parent';
}

function ProClientLayout() {
  const { inviteId } = Route.useParams();
  const id = Number(inviteId);
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const clientsQuery = useQuery({
    queryKey: ['professional-clients'],
    queryFn: () => apiJson<ProfessionalClientListResponse>(getToken, '/v1/professional/clients'),
    enabled: isLoaded && !!isSignedIn,
  });

  const invite = useMemo(
    () => clientsQuery.data?.clients.find((c) => c.id === id),
    [clientsQuery.data, id]
  );

  const bodyStyle = {
    fontFamily:
      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  } as const;

  const base = `/pro/clients/${inviteId}`;

  const tabClass = (suffix: string) => {
    const full = `${base}${suffix}`;
    const active = pathname === full || pathname === `${full}/`;
    return active
      ? 'border-[#018A9D] text-[#018A9D] font-semibold'
      : 'border-transparent text-gray-600 hover:text-gray-900';
  };

  if (clientsQuery.isLoading || !isLoaded) {
    return (
      <div className='max-w-4xl mx-auto px-6 py-12 flex justify-center' style={bodyStyle}>
        <div className='animate-spin h-8 w-8 border-2 border-[#018A9D] border-t-transparent rounded-full' />
      </div>
    );
  }

  if (!invite) {
    return (
      <div className='max-w-4xl mx-auto px-6 py-12' style={bodyStyle}>
        <p className='text-gray-600 mb-4'>Client not found or you no longer have access.</p>
        <Link to='/pro' className='text-[#018A9D] font-semibold'>
          Back to dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-sky-light' style={bodyStyle}>
      <div className='max-w-4xl mx-auto px-6 py-8'>
        <Link
          to='/pro'
          className='inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900 mb-6'
        >
          <FiArrowLeft className='w-4 h-4' />
          Dashboard
        </Link>

        <h1 className='text-2xl font-semibold text-gray-900 mb-1'>{coparentDisplayName(invite)}</h1>
        <p className='text-gray-500 text-sm mb-6'>Read-only shared records</p>

        <nav className='flex flex-wrap gap-2 border-b border-gray-200 mb-6'>
          {invite.share_chats ? (
            <Link
              to='/pro/clients/$inviteId/chat'
              params={{ inviteId }}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/chat')}`}
            >
              Chat
            </Link>
          ) : null}
          {invite.share_expenses ? (
            <Link
              to='/pro/clients/$inviteId/expenses'
              params={{ inviteId }}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/expenses')}`}
            >
              Expenses
            </Link>
          ) : null}
          {invite.share_custody_calendar ? (
            <Link
              to='/pro/clients/$inviteId/calendar'
              params={{ inviteId }}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/calendar')}`}
            >
              Calendar
            </Link>
          ) : null}
          {invite.share_journal ? (
            <Link
              to='/pro/clients/$inviteId/journal'
              params={{ inviteId }}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${tabClass('/journal')}`}
            >
              Journal
            </Link>
          ) : null}
        </nav>

        <Outlet />
      </div>
    </div>
  );
}

export const Route = createFileRoute('/pro/clients/$inviteId')({
  component: ProClientLayout,
});
